import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

// REAL DATA: monthly income is derived from this vendor's actual (already-fetched)
// orders — no synthetic values. Last 6 calendar months, cancelled orders excluded.
const buildIncomeData = (orders) => {
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const out = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const m = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ key: `${m.getFullYear()}-${m.getMonth()}`, month: MONTHS[m.getMonth()], income: 0 });
  }
  for (const o of orders || []) {
    if (['Cancelled', 'Returned'].includes(o.status)) continue;
    const d = new Date(o.createdAt);
    const row = out.find(r => r.key === `${d.getFullYear()}-${d.getMonth()}`);
    if (row) row.income += Number(o.totalAmount) || 0;
  }
  return out;
};

const VendorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVendorDetails = async () => {
      try {
        const [vendorRes, productsRes, ordersRes] = await Promise.all([
          api.get('/admin/vendors/' + id),
          api.get(`/admin/products?vendorId=${id}`),
          api.get(`/admin/orders?vendorId=${id}`)
        ]);
        if (vendorRes.data.success) {
          setVendor(vendorRes.data.data);
        }
        if (productsRes.data.success) {
          setProducts(productsRes.data.data);
        }
        if (ordersRes.data.success) {
          setOrders(ordersRes.data.data);
        }
      } catch (error) {
        console.error("Error fetching vendor details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVendorDetails();
  }, [id]);

  const handleDownloadStatement = () => {
    const csvContent = "Month,Income\n" + buildIncomeData(orders).map(d => `${d.month},${d.income}`).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `vendor_statement_${id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div className="p-8 text-center font-bold text-stone-500">Loading Vendor Profile...</div>;
  if (!vendor) return <div className="p-8 text-center font-bold text-red-500">Vendor Not Found</div>;

  const name = vendor.userId?.name || vendor.name || 'Unknown Vendor';
  const status = vendor.approved ? 'Active' : 'Pending';

  return (
    <div className="p-8 bg-[#FAF9F6] min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-stone-400 hover:text-[#2A2858] transition-colors">
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <div>
            <h2 className="font-serif text-3xl font-bold text-[#2A2858]">{name}</h2>
            <p className="mt-1 text-stone-500 text-sm">Vendor Profile & Financial Analytics</p>
          </div>
        </div>
        <button
          onClick={handleDownloadStatement}
          className="bg-[#2A2858] text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg hover:bg-[#14122B] transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-sm">download</span> Download Statement
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Info Card */}
        <div className="lg:col-span-1 bg-white p-6 rounded-2xl border border-[#E8B84B]/20 shadow-sm">
          <h3 className="font-serif text-xl font-bold text-[#2A2858] mb-4 border-b border-stone-100 pb-2">Profile Info</h3>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold text-stone-400 uppercase tracking-widest">Category</label>
              <p className="font-medium text-stone-800">{vendor.type || 'Vendor'}</p>
            </div>
            <div>
              <label className="text-xs font-bold text-stone-400 uppercase tracking-widest">Email</label>
              <p className="font-medium text-stone-800">{vendor.userId?.email || 'N/A'}</p>
            </div>
            <div>
              <label className="text-xs font-bold text-stone-400 uppercase tracking-widest">Status</label>
              <div className="mt-1">
                <span className={`px-3 py-1 rounded-full text-xs font-bold border ${vendor.approved ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                  {status}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Chart Card */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-stone-100 shadow-sm">
          <h3 className="font-serif text-xl font-bold text-stone-800 mb-6">Income History</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={buildIncomeData(orders)}>
                <defs>
                  <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2A2858" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#2A2858" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#a8a29e', fontSize: 12 }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#a8a29e', fontSize: 12 }} tickFormatter={(val) => `₹${val}`} />
                <Tooltip
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
                />
                <Area type="monotone" dataKey="income" stroke="#2A2858" strokeWidth={3} fillOpacity={1} fill="url(#colorIncome)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        {/* Products List */}
        <div className="bg-white p-6 rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
          <h3 className="font-serif text-xl font-bold text-stone-800 mb-6 flex justify-between items-center">
            Vendor Products
            <span className="bg-stone-100 text-stone-600 px-3 py-1 rounded-lg text-sm font-bold">{products.length} Items</span>
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead>
                <tr className="text-stone-400 font-bold uppercase tracking-wider text-xs border-b border-stone-100">
                  <th className="pb-4 font-bold">Product</th>
                  <th className="pb-4 font-bold">Price</th>
                  <th className="pb-4 font-bold">Stock</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {products.length > 0 ? products.map(product => (
                  <tr key={product._id} className="hover:bg-stone-50/50 transition-colors">
                    <td className="py-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center overflow-hidden border border-stone-200">
                        {product.image ? <img src={product.image} alt={product.name} className="w-full h-full object-cover" /> : <span className="material-symbols-outlined text-stone-400">inventory_2</span>}
                      </div>
                      <span className="font-bold text-stone-800">{product.name}</span>
                    </td>
                    <td className="py-4 font-bold text-[#201D42]">₹{product.price}</td>
                    <td className="py-4"><span className="px-2.5 py-1 bg-stone-100 text-stone-700 font-bold rounded-md">{product.stock} in stock</span></td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="3" className="py-8 text-center text-stone-400">No products found for this vendor.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Orders List */}
        <div className="bg-white p-6 rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
          <h3 className="font-serif text-xl font-bold text-stone-800 mb-6 flex justify-between items-center">
            Recent Orders
            <span className="bg-stone-100 text-stone-600 px-3 py-1 rounded-lg text-sm font-bold">{orders.length} Orders</span>
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead>
                <tr className="text-stone-400 font-bold uppercase tracking-wider text-xs border-b border-stone-100">
                  <th className="pb-4 font-bold">Product</th>
                  <th className="pb-4 font-bold">Amount</th>
                  <th className="pb-4 font-bold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {orders.length > 0 ? orders.map(order => (
                  <tr key={order._id} className="hover:bg-stone-50/50 transition-colors">
                    <td className="py-4">
                      <p className="font-bold text-stone-800">{order.productName || order.productId?.name || 'Item'}</p>
                      <p className="text-[10px] text-stone-500">{order.quantity}x • {new Date(order.createdAt).toLocaleDateString()}</p>
                    </td>
                    <td className="py-4 font-bold text-[#201D42]">₹{order.totalAmount}</td>
                    <td className="py-4">
                      <span className="px-2 py-1 rounded text-[10px] font-bold border border-stone-200 text-stone-600">
                        {order.status}
                      </span>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="3" className="py-8 text-center text-stone-400">No orders found for this vendor.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDetails;
